import {
  Box,
  HStack,
  Image,
  Link,
  Tooltip,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Button,
} from "@chakra-ui/react";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link as ReactRouterLink } from "react-router-dom";
import ResponsiveNavigation from "../components/ResponsiveNavigation";
import { useLocaleContext } from "../providers/LocaleProvider";
import { useSelector } from "../store";
import { home, settings, logout, earth } from "../assets/icons";
import {
  LEARN_ROUTE,
  TRAIN_ROUTE,
  LOGOUT_ROUTE,
  SETTINGS_ROUTE,
  PLAYLISTS_ROUTE,
} from "../routes";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import CastForEducationIcon from "@mui/icons-material/CastForEducation";
import QueueMusicIcon from "@mui/icons-material/QueueMusic";
import useViewPortWidth from "../utils/use-viewport-width";
import { getSectionStyles } from "../views/Portal/portalUtils";
import LightThanosBar from "../components/ThanosBar/LightThanosBar";

const DarkHeader = () => {
  const [open, setOpen] = useState<boolean>(false);
  const intl = useIntl();
  const { locale, setLocale } = useLocaleContext();
  const user = useSelector((state) => state.auth.user);
  const { viewPortWidth } = useViewPortWidth();
  const { width: sectionWidth } = getSectionStyles(viewPortWidth);

  const currentUrl = window.location.href;
  const isLargeScreen = viewPortWidth >= 920;

  const isArabic = locale === "ar";

  const showLightThanosBar =
    !currentUrl.includes("/portal") &&
    user &&
    user.subscriptionStatus === "approved";

  // this section contains the logout, language, settings, and home buttons
  const fourBtnSection = () => (
    <Box
      flex="1"
      display={["none", "none", "flex", "flex"]}
      justifyContent="space-between"
      alignItems="center"
      width={isLargeScreen ? "15.5rem" : "11.5rem"}
      maxWidth={isLargeScreen ? "15.5rem" : "11.5rem"}
    >
      <HStack
        spacing={isLargeScreen ? 9 : 7}
        justifyContent="center"
        width="100%"
        maxWidth="100%"
      >
        {/* Logout Button */}
        {user && user?.subscriptionStatus === "approved" && (
          <Tooltip
            hasArrow
            label={intl.formatMessage({ id: "logout" })}
            arrowSize={8}
            bg="#1f3f93"
            color="white"
          >
            <Link as={ReactRouterLink} to={LOGOUT_ROUTE}>
              <Image src={logout} maxW="unset" width="24px" height="24px" />
            </Link>
          </Tooltip>
        )}

        {/* Language Button */}
        <Tooltip
          hasArrow
          label={intl.formatMessage({ id: "changelanguage" })}
          isDisabled={open}
          arrowSize={8}
          bg="#1f3f93"
          color="white"
        >
          <Link>
            <Popover
              onOpen={() => setOpen(true)}
              isOpen={open}
              onClose={() => setOpen(false)}
              isLazy
              variant="responsive"
            >
              <PopoverTrigger>
                <Image src={earth} maxW="unset" width="24px" height="24px" />
              </PopoverTrigger>
              <PopoverContent borderRadius="15px">
                <PopoverArrow />
                <PopoverBody
                  p="1"
                  color="gray.900"
                  fontSize="18px"
                  fontFamily="Calibri (Body)"
                >
                  <HStack justifyContent="center">
                    <Button
                      backgroundColor={locale === "ar" ? "#1f3f93" : ""}
                      textColor={locale === "ar" ? "white" : ""}
                      onClick={() => {
                        setOpen(false);
                        setLocale("ar");
                      }}
                    >
                      <FormattedMessage id="ar" />
                    </Button>
                    <Button
                      backgroundColor={locale === "en" ? "#1f3f93" : ""}
                      textColor={locale === "en" ? "white" : ""}
                      onClick={() => {
                        setOpen(false);
                        setLocale("en");
                      }}
                    >
                      <FormattedMessage id="en" />
                    </Button>
                  </HStack>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Link>
        </Tooltip>

        {/* Settings Button */}
        {user?.subscriptionStatus === "approved" && (
          <Tooltip
            hasArrow
            label={intl.formatMessage({ id: "settings" })}
            arrowSize={8}
            bg="#1f3f93"
            color="white"
          >
            <Link as={ReactRouterLink} to={SETTINGS_ROUTE}>
              <Image src={settings} maxW="unset" width="24px" height="24px" />
            </Link>
          </Tooltip>
        )}

        {/* Home Button */}
        {user?.subscriptionStatus === "approved" && (
          <Tooltip
            hasArrow
            label={intl.formatMessage({ id: "homebutton" })}
            arrowSize={8}
            bg="#1f3f93"
            color="white"
          >
            <Link
              as={ReactRouterLink}
              to="/portal"
              _focus={{ outline: "none", border: "none" }}
              _hover={{ textDecoration: "none" }}
            >
              <Image src={home} maxW="unset" width="24px" height="24px" />
            </Link>
          </Tooltip>
        )}
      </HStack>
    </Box>
  );

  // this section contains the playlists, training, and learning buttons
  const threeBtnSection = () => (
    <Box
      flex="1"
      display={["none", "none", "flex", "flex"]}
      justifyContent="space-between"
      alignItems="center"
      paddingLeft={
        isArabic
          ? isLargeScreen
            ? "1.5em"
            : "0"
          : isLargeScreen
          ? "3.7em"
          : "1.75em"
      }
      paddingRight={isArabic ? (isLargeScreen ? "2em" : "2.5em") : "0"}
      width={isLargeScreen ? "12rem" : "10rem"}
      maxWidth={isLargeScreen ? "12rem" : "10rem"}
      marginRight={isArabic ? "0" : "2em"}
      marginLeft={isArabic ? "2em" : "0"}
      transform="translateY(10%)"
    >
      <HStack spacing={9} justifyContent="center" width="100%" maxWidth="100%">
        {/* Playlists Button */}
        {user?.subscriptionStatus === "approved" && (
          <Tooltip
            hasArrow
            label={intl.formatMessage({ id: "playlists" })}
            arrowSize={8}
            bg="#1f3f93"
            color="white"
          >
            <Link as={ReactRouterLink} to={PLAYLISTS_ROUTE}>
              <QueueMusicIcon
                style={{
                  fontSize: 24,
                  width: "24px",
                  height: "24px",
                  color: "#1f3f93",
                }}
              />
            </Link>
          </Tooltip>
        )}

        {/* Training Button */}
        {user?.subscriptionStatus === "approved" && (
          <Tooltip
            hasArrow
            label={intl.formatMessage({ id: "training" })}
            arrowSize={8}
            bg="#1f3f93"
            color="white"
          >
            <Link as={ReactRouterLink} to={TRAIN_ROUTE}>
              <CastForEducationIcon
                style={{
                  fontSize: 24,
                  width: "24px",
                  height: "24px",
                  color: "#1f3f93",
                }}
              />
            </Link>
          </Tooltip>
        )}

        {/* Learning Button */}
        {user?.subscriptionStatus === "approved" && (
          <Tooltip
            hasArrow
            label={intl.formatMessage({ id: "learning" })}
            arrowSize={8}
            bg="#1f3f93"
            color="white"
          >
            <Link as={ReactRouterLink} to={LEARN_ROUTE}>
              <LocalLibraryIcon
                style={{
                  fontSize: 24,
                  width: "24px",
                  height: "24px",
                  color: "#1f3f93",
                }}
              />
            </Link>
          </Tooltip>
        )}
      </HStack>
    </Box>
  );

  return (
    <Box maxWidth="1200px" margin="0 auto" width="100%">
      <Box
        as="nav"
        px={["0px", "0px", "0px", "0px"]}
        display="flex"
        justifyContent="center"
        flexDirection={["column", "column", "row", "row", "row"]}
        position="relative"
        pt={{
          base: "20px",
          md: "30px",
        }}
        alignItems={["center", "center", "center", "center", "center"]}
        dir="ltr" // Force LTR direction to prevent button swapping
      >
        {/* Left Navigation Buttons - Fixed order */}
        <Box order={1}>{isArabic ? fourBtnSection() : threeBtnSection()}</Box>

        {/* Center Logo - Fixed order */}
        <Box
          order={2}
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          margin={["0 40px", "0 40px", "0 40px", "0 40px"]}
          transform={
            viewPortWidth > 920
              ? "translateX(5%)"
              : viewPortWidth >= 768 && viewPortWidth <= 920
              ? "translateX(-1.75%)"
              : "translateX(0)"
          }
          width={sectionWidth}
        >
          <ReactRouterLink to="/portal">
            <Image
              loading="lazy"
              alt="SAL Logo"
              src="/final-logo-SAL.png"
              marginTop={["10px", "10px", "0px", "10px"]}
              height={["30px", "32px", "36px", "40px"]}
              display="flex"
            />
          </ReactRouterLink>
        </Box>

        {/* Right Navigation Buttons - Fixed order */}
        <Box order={3}>{isArabic ? threeBtnSection() : fourBtnSection()}</Box>

        {/* Mobile Navigation - Only hamburger menu on small screens */}
        <Box
          display={["block", "block", "none", "none"]}
          position="absolute"
          top="20px"
          right="20px"
        >
          <ResponsiveNavigation />
        </Box>
      </Box>
      {/* LightGem Buttons (LightThanosBar) - show for users with approved subscription */}
      {showLightThanosBar && (
        <Box width="100%" display="flex" justifyContent="center" my={3} mt={5}>
          <LightThanosBar />
        </Box>
      )}
    </Box>
  );
};

export default DarkHeader;
